import { create } from 'zustand';
import { EditorState, SceneObject } from '../types';
import { Game, GameScene, emptyGame, emptyScene } from '../types/game';

interface EditorStore extends EditorState {
  // --- Live editing state (unchanged surface used by the editor) ---
  sceneObjects: SceneObject[];
  addObject: (obj: SceneObject) => void;
  updateObject: (uuid: string, updates: Partial<SceneObject>) => void;
  removeObject: (uuid: string) => void;
  selectObject: (uuid: string | null) => void;
  setPlaying: (playing: boolean) => void;
  setShowGrid: (show: boolean) => void;
  setCameraFov: (fov: number) => void;
  setZoom: (zoom: number) => void;
  clearScene: () => void;
  loadScene: (objects: SceneObject[]) => void;

  // --- Game model (the spine) ---
  game: Game;
  activeSceneId: string;
  // Replace the whole game (used on load once the backend persists full games)
  loadGame: (game: Game) => void;
  // Assemble the current full game (active scene's live objects merged in) for saving
  getGame: () => Game;
  // Update top-level game fields (title, concept, narrative, economy, publish, ...)
  setGameMeta: (updates: Partial<Game>) => void;

  // --- Scene management ---
  commitSceneObjects: (sceneId: string, objects: SceneObject[]) => void;
  addScene: (name?: string) => string;
  deleteScene: (sceneId: string) => void;
  setActiveSceneId: (sceneId: string) => void;
}

export const useStore = create<EditorStore>((set, get) => ({
  // live editing state
  sceneObjects: [],
  selectedObjectId: null,
  isPlaying: false,
  showGrid: true,
  cameraFov: 75,
  zoom: 1,

  // game model
  game: emptyGame(),
  activeSceneId: 'scene_1',

  addObject: (obj) =>
    set((state) => ({ sceneObjects: [...state.sceneObjects, obj] })),
  updateObject: (uuid, updates) =>
    set((state) => ({
      sceneObjects: state.sceneObjects.map((obj) =>
        obj.uuid === uuid ? { ...obj, ...updates } : obj
      ),
    })),
  removeObject: (uuid) =>
    set((state) => {
      // Collect the object plus every descendant (children, grandchildren…).
      const toRemove = new Set<string>([uuid]);
      let changed = true;
      while (changed) {
        changed = false;
        for (const o of state.sceneObjects) {
          if (o.parentId && toRemove.has(o.parentId) && !toRemove.has(o.uuid)) {
            toRemove.add(o.uuid);
            changed = true;
          }
        }
      }
      return {
        sceneObjects: state.sceneObjects.filter((obj) => !toRemove.has(obj.uuid)),
        selectedObjectId:
          state.selectedObjectId && toRemove.has(state.selectedObjectId)
            ? null
            : state.selectedObjectId,
      };
    }),
  selectObject: (uuid) => set(() => ({ selectedObjectId: uuid })),
  setPlaying: (playing) => set(() => ({ isPlaying: playing })),
  setShowGrid: (show) => set(() => ({ showGrid: show })),
  setCameraFov: (fov) => set(() => ({ cameraFov: fov })),
  setZoom: (zoom) => set(() => ({ zoom })),
  clearScene: () => set(() => ({ sceneObjects: [], selectedObjectId: null })),
  loadScene: (objects) =>
    set(() => ({
      sceneObjects: Array.isArray(objects) ? objects : [],
      selectedObjectId: null,
    })),

  // --- Game model actions ---
  loadGame: (game) =>
    set(() => {
      const scenes: GameScene[] =
        game.scenes && game.scenes.length ? game.scenes : [emptyScene()];
      const active = scenes[0];
      return {
        game: { ...game, scenes },
        activeSceneId: active.id,
        sceneObjects: Array.isArray(active.objects) ? active.objects : [],
        selectedObjectId: null,
      };
    }),

  getGame: () => {
    const state = get();
    const scenes = state.game.scenes.length
      ? state.game.scenes
      : [emptyScene({ id: state.activeSceneId })];
    const merged = scenes.map((s) =>
      s.id === state.activeSceneId ? { ...s, objects: state.sceneObjects } : s
    );
    return { ...state.game, scenes: merged, updatedAt: new Date().toISOString() };
  },

  setGameMeta: (updates) =>
    set((state) => ({ game: { ...state.game, ...updates } })),

  commitSceneObjects: (sceneId, objects) =>
    set((state) => ({
      game: {
        ...state.game,
        scenes: state.game.scenes.map((s) =>
          s.id === sceneId ? { ...s, objects } : s
        ),
      },
    })),

  addScene: (name) => {
    const id = 'scene_' + Math.random().toString(36).slice(2, 8);
    set((state) => ({
      game: {
        ...state.game,
        scenes: [
          ...state.game.scenes,
          emptyScene({
            id,
            name: name || `Scene ${state.game.scenes.length + 1}`,
          }),
        ],
      },
    }));
    return id;
  },

  deleteScene: (sceneId) =>
    set((state) => {
      if (state.game.scenes.length <= 1) return {};
      return {
        game: {
          ...state.game,
          scenes: state.game.scenes.filter((s) => s.id !== sceneId),
        },
      };
    }),

  setActiveSceneId: (sceneId) => set(() => ({ activeSceneId: sceneId })),
}));
